import apiService from '@/services/api';

const ESEWA_CONTEXT_KEY = 'ESEWA_PAYMENT_CONTEXT';

export const readEsewaContext = () => {
  try {
    const raw = localStorage.getItem(ESEWA_CONTEXT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const clearEsewaContext = () => {
  try {
    localStorage.removeItem(ESEWA_CONTEXT_KEY);
  } catch {
    // no-op
  }
};

const persistEsewaContext = (context) => {
  try {
    localStorage.setItem(ESEWA_CONTEXT_KEY, JSON.stringify(context));
  } catch {
    // no-op
  }
};

const pick = (obj, keys, fallback = '') => {
  for (const key of keys) {
    if (obj && obj[key] !== undefined && obj[key] !== null && obj[key] !== '') {
      return obj[key];
    }
  }
  return fallback;
};

const toEsewaAmount = (value) => {
  const str = String(value ?? '').trim();
  if (!str) return '';
  if (str.includes('.')) return str; // already major unit
  const n = Number(str);
  if (Number.isNaN(n)) return str;
  // Backend subscription prices come in minor units (e.g., 599000). eSewa expects major units.
  // Keep plain numeric string (e.g., 5990) instead of forcing trailing decimals.
  const divided = n / 100;
  return Number.isInteger(divided) ? String(divided) : String(divided);
};

const createAndSubmitEsewaForm = (refData) => {
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = refData.payActionUrl;
  form.style.display = 'none';

  // Prefer exact keys from init response payload.
  const amount = pick(refData, ['amount']);
  const taxAmount = pick(refData, ['taxAmount', 'tax_amount'], 0);
  const totalAmountRaw = pick(refData, ['totalAmount', 'total_amount'], amount);
  const transactionUuid = pick(refData, ['transactionuUuid', 'transaction_uuid', 'transactionUuid']);
  const productCode = pick(refData, ['productCode', 'product_code']);
  const productServiceCharge = pick(refData, ['productServiceCharge', 'product_service_charge'], 0);
  const productDeliveryCharge = pick(refData, ['productDeliveryCharge', 'product_delivery_charge'], 0);
  const signedFieldNames = pick(refData, ['signedFieldNames', 'signed_field_names']);
  const successUrl = pick(refData, ['successUrl', 'success_url'], '');
  const failureUrl = pick(refData, ['failureUrl', 'failure_url'], '');
  const signature = pick(refData, ['signature'], '');

  const fields = {
    amount: toEsewaAmount(amount),
    tax_amount: toEsewaAmount(taxAmount),
    total_amount: toEsewaAmount(totalAmountRaw),
    transaction_uuid: String(transactionUuid || ''),
    product_code: String(productCode || ''),
    product_service_charge: toEsewaAmount(productServiceCharge),
    product_delivery_charge: toEsewaAmount(productDeliveryCharge),
    success_url: String(successUrl),
    failure_url: String(failureUrl),
    signed_field_names: String(signedFieldNames || ''),
    signature: String(signature),
  };

  Object.entries(fields).forEach(([name, value]) => {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    form.appendChild(input);
  });

  document.body.appendChild(form);
  form.submit();
};

export const startEsewaSubscriptionCheckout = async (plan) => {
  const availabilityId = Array.isArray(plan?.availabilityset) ? plan.availabilityset[0] : '';
  if (!plan?.planId || !availabilityId) {
    throw new Error('Plan details are incomplete for payment.');
  }

  const origin =
    typeof window !== 'undefined' && window.location?.origin
      ? window.location.origin
      : '';
  const successUrl = origin ? `${origin}/payment/success` : '';
  const failureUrl = origin ? `${origin}/payment/failed` : '';

  const response = await apiService.initEsewaPayment({
    devicetype: 'WEB',
    amount: plan.amount,
    currency: plan.currency || 'NPR',
    transactionpurpose: 'SUBSCRIPTION',
    transactionmode: 'WA',
    planid: plan.planId,
    availabilityid: availabilityId,
    successUrl,
    failureUrl,
  });

  const referenceid = response?.data?.referenceid;
  const referencedata = response?.data?.referencedata;

  if (!referenceid || !referencedata?.payActionUrl) {
    throw new Error('Invalid payment init response from server.');
  }

  persistEsewaContext({
    referenceid,
    referencedata,
    createdAt: Date.now(),
  });

  createAndSubmitEsewaForm(referencedata);
};
